<template>
  <div class="container">
    <van-nav-bar :title="$t('recharge.title')">
      <template #left>
        <van-icon name="arrow-left" color="#fff" size="20" @click="back()"></van-icon>
      </template>
      <template #right>
        <div class="record" @click="goRecord">{{ $t('recharge.recharge_record') }}</div>
      </template>
    </van-nav-bar>

    <div class="header">
      <div class="balance-card">
        <div class="balance-label">{{ $t('recharge.curr_balance') }}</div>
        <div class="balance-value">{{ balanceText }}</div>
      </div>
    </div>

    <div class="content">
      <div v-if="pending && pending.pay_url && !payVisible" class="pending-bar">
        <span>{{ $t('recharge.pending_order') }}</span>
        <span class="pending-action" @click="openPendingPay">{{ $t('recharge.continue_pay') }}</span>
      </div>

      <div class="card">
        <div class="card-title">{{ $t('recharge.select_amount') }}</div>

        <div v-if="configLoading" class="config-loading">
          <van-loading size="18" color="#c3a3f2" />
          <span>{{ $t('common.loading') }}</span>
        </div>

        <div v-else-if="config.amounts.length" class="amount-grid">
          <div
            v-for="item in config.amounts"
            :key="item"
            class="amount-item"
            :class="{ active: isSelected(item) }"
            @click="pickAmount(item)"
          >{{ cleanAmount(item) }}</div>
        </div>
        <div v-else class="amount-empty">{{ $t('recharge.config_error') }}</div>

        <div class="card-title custom-title">{{ $t('recharge.custom_amount') }}</div>
        <div class="input-wrap">
          <van-field
            v-model="money"
            type="number"
            :placeholder="$t('recharge.input_money')"
            @input="onMoneyInput"
          />
        </div>
        <div v-if="hasRange" class="range-tip">
          {{ $t('recharge.min') }} {{ cleanAmount(config.min_money) }} ·
          {{ $t('recharge.max') }} {{ cleanAmount(config.max_money) }}
        </div>

        <div v-if="tipsText" class="tips">{{ tipsText }}</div>
      </div>

      <div class="button" :class="{ disabled: configLoading || !configReady || submitting }" @click="onSubmit">
        <van-loading v-if="submitting" size="18" color="#fff" />
        <span v-if="submitting" class="button-text">{{ $t('recharge.processing') }}</span>
        <span v-else>{{ $t('recharge.pay_now') }}</span>
      </div>
    </div>

    <!-- 支付页用 iframe 内嵌，支付完成后由全局 WS 推送到账再自动关闭 -->
    <div v-if="payVisible" class="pay-mask">
      <div class="pay-panel">
        <div class="pay-head">
          <span>{{ $t('recharge.pay_page') }}</span>
          <van-icon name="cross" color="#4a3a5c" size="20" @click="closePayFrame"></van-icon>
        </div>
        <iframe
          v-if="payUrl"
          :src="payUrl"
          class="pay-iframe"
          frameborder="0"
          scrolling="auto"
          allow="clipboard-read; clipboard-write"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import { Dialog } from 'vant'
import { onMessage } from '@/common/ws'
import {
  clearPendingOrder,
  getCachedPayUrl,
  getPendingOrder,
  isOrderExpired,
  savePayCache,
  setPendingOrder,
  stripCurrency
} from '@/common/recharge'



export default {
  data() {
    return {
      balance: 0,
      configLoading: true,
      configReady: false,
      config: {
        amounts: [],
        min_money: null,
        max_money: null,
        currency: 'BRL',
        tips: ''
      },
      money: '',
      submitting: false,
      pending: null,
      payVisible: false,
      payUrl: '',
      paidOrders: {},
      offWs: null
    }
  },
  computed: {
    balanceText() {
      return this.changeMoney(stripCurrency(this.balance))
    },
    hasRange() {
      const min = this.config.min_money
      const max = this.config.max_money
      return (
        min !== null && min !== undefined && max !== null && max !== undefined &&
        Number.isFinite(Number(stripCurrency(min))) &&
        Number.isFinite(Number(stripCurrency(max)))
      )
    },
    tipsText() {
      const serverTips = String(this.config.tips || '').trim()
      const localTips = this.$t('recharge.pay_tip')
      if (!serverTips) return localTips
      // 服务端默认英文提示在非英文环境下用本地翻译，避免巴西用户看到英文
      if (
        this.$i18n.locale !== 'en_us' &&
        serverTips === 'Your balance will be credited automatically after payment'
      ) {
        return localTips
      }
      return serverTips
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.replace({ path: '/Login' })
      return
    }
    // 先订阅，避免连接补发的到账消息在页面初始化期间漏掉
    this.offWs = onMessage(this.handleWsMessage)
    this.loadBalance()
    this.loadConfig()
    this.resumePendingOrder()
  },
  beforeDestroy() {
    if (this.offWs) {
      this.offWs()
      this.offWs = null
    }
  },
  methods: {
    back() {
      return window.history.back()
    },
    goRecord() {
      this.$router.push('/ChongRecord')
    },
    handleWsMessage(msg) {
      if (msg && msg.type === 'recharge' && msg.event === 'paid') {
        this.handleRechargePaid(msg.data || {})
      }
    },
    // 仅做一次状态补偿，不轮询
    checkOrderStatusOnce(orderNo) {
      if (!orderNo) return
      this.$http({
        method: 'get',
        url: 'recharge_status',
        data: { order_no: orderNo }
      }).then(res => {
        if (res && res.code === 200 && res.data) {
          const status = Number(res.data.status)
          if (status === 1) {
            this.handleRechargePaid(res.data)
          } else if (status === 3) {
            this.clearPendingState()
            this.$toast(this.$t('recharge.pay_failed'))
          }
        }
      })
    },
    handleRechargePaid(data) {
      const orderNo = data && data.order_no
      if (!orderNo) return
      const isCurrent = this.pending && this.pending.order_no === orderNo
      if (this.paidOrders[orderNo]) {
        // 首条推送可能早于本地 pending 恢复，等 pending 对上后再关闭支付页
        if (isCurrent) this.onPaid(data)
        return
      }
      this.paidOrders[orderNo] = true
      const money = data.money ? stripCurrency(data.money) : ''
      if (isCurrent) {
        this.onPaid(data)
        return
      }
      this.loadBalance()
      const message = money
        ? `${this.$t('recharge.pay_success')} +${money}`
        : this.$t('recharge.pay_success')
      this.$toast.success(message)
    },
    loadBalance() {
      this.$http({
        method: 'get',
        url: 'user_info'
      }).then(res => {
        if (res && res.code === 200 && res.data) {
          if (res.data.status !== undefined && Number(res.data.status) !== 1) {
            this.$toast(this.$t('video.account_out'))
            localStorage.clear()
            this.$router.replace({ path: '/Login' })
            return
          }
          this.balance = res.data.money || 0
          this.$store.commit('setUserInfoValue', res.data)
        } else if (res && res.code === 401) {
          this.handleUnauthorized(res)
        }
      })
    },
    loadConfig() {
      this.configLoading = true
      this.$http({
        method: 'get',
        url: 'recharge_config'
      }).then(res => {
        this.configLoading = false
        if (res && res.code === 200 && res.data) {
          const data = res.data || {}
          const amounts = Array.isArray(data.amounts) ? data.amounts : []
          this.config = {
            amounts,
            min_money: data.min_money === undefined || data.min_money === null ? null : Number(data.min_money),
            max_money: data.max_money === undefined || data.max_money === null ? null : Number(data.max_money),
            currency: data.currency || 'BRL',
            tips: data.tips || ''
          }
          this.configReady = true
          if (!this.money && amounts.length) {
            this.money = stripCurrency(amounts[0])
          }
          return
        }
        this.configReady = false
        if (res && res.code === 401) {
          this.handleUnauthorized(res)
          return
        }
        const fallback = res && Number(res.code) === 4001
          ? this.$t('recharge.channel_maintenance')
          : this.$t('recharge.config_error')
        this.$toast((res && res.msg) || fallback)
      }).catch(() => {
        this.configLoading = false
        this.configReady = false
        this.$toast(this.$t('recharge.config_error'))
      })
    },
    cleanAmount(value) {
      return stripCurrency(value)
    },
    isSelected(item) {
      return String(this.money) === String(stripCurrency(item))
    },
    pickAmount(item) {
      this.money = stripCurrency(item)
    },
    onMoneyInput(value) {
      this.money = stripCurrency(value).replace(/\s/g, '')
    },
    validateAmount() {
      const raw = String(this.money === null || this.money === undefined ? '' : this.money).trim()
      if (!raw || !/^\d+$/.test(raw)) {
        this.$toast(this.$t(raw && raw.indexOf('.') > -1 ? 'recharge.integer_error' : 'recharge.invalid_amount'))
        return null
      }
      const amount = Number(raw)
      if (!Number.isFinite(amount) || amount <= 0) {
        this.$toast(this.$t('recharge.invalid_amount'))
        return null
      }
      const min = this.config.min_money === null || this.config.min_money === undefined
        ? NaN
        : Number(stripCurrency(this.config.min_money))
      const max = this.config.max_money === null || this.config.max_money === undefined
        ? NaN
        : Number(stripCurrency(this.config.max_money))
      if (
        (Number.isFinite(min) && amount < min) ||
        (Number.isFinite(max) && amount > max)
      ) {
        this.$toast(this.$t('recharge.range_error', { min, max }))
        return null
      }
      return amount
    },
    onSubmit() {
      if (this.configLoading || !this.configReady) {
        this.$toast(this.$t('recharge.config_error'))
        return
      }
      if (this.submitting) return
      const amount = this.validateAmount()
      if (amount === null) return

      this.submitting = true
      this.$http({
        method: 'post',
        url: 'recharge_create',
        data: { amount }
      }).then(res => {
        this.submitting = false
        if (res && res.code === 200 && res.data && res.data.pay_url) {
          const order = res.data
          setPendingOrder(order)
          savePayCache(order)
          this.pending = {
            order_no: order.order_no,
            pay_url: order.pay_url,
            expire_at: Number(order.expire_at) || 0
          }
          this.openPayFrame(order.pay_url)
          return
        }
        if (res && res.code === 401) {
          this.handleUnauthorized(res)
          return
        }
        this.$toast((res && res.msg) || this.$t('recharge.create_failed'))
      }).catch(() => {
        this.submitting = false
        this.$toast(this.$t('recharge.create_failed'))
      })
    },
    openPayFrame(url) {
      if (!url) return
      this.payUrl = url
      this.payVisible = true
    },
    openPendingPay() {
      if (!this.pending || !this.pending.pay_url) return
      this.openPayFrame(this.pending.pay_url)
    },
    closePayFrame() {
      // 只关闭内嵌支付页，订单仍保留，等 WS 推送或重新打开
      this.payVisible = false
      this.payUrl = ''
    },
    resumePendingOrder() {
      let order = getPendingOrder()
      const queryOrderNo = this.$route.query && this.$route.query.order_no
      const cachedUrl = queryOrderNo ? getCachedPayUrl(queryOrderNo) : ''
      if (queryOrderNo && (!order || order.order_no !== queryOrderNo)) {
        order = { order_no: queryOrderNo, pay_url: cachedUrl, expire_at: 0 }
      }
      if (!order || !order.order_no) return
      if (isOrderExpired(order)) {
        this.clearPendingState()
        if (queryOrderNo) this.$toast(this.$t('recharge.order_expired'))
        return
      }
      // 没有有效期且已放置超过 2 小时，视为无效缓存
      if (!Number(order.expire_at) && order.created_at && Date.now() - Number(order.created_at) > 2 * 60 * 60 * 1000) {
        this.clearPendingState()
        return
      }
      this.pending = {
        order_no: order.order_no,
        pay_url: order.pay_url || cachedUrl || '',
        expire_at: Number(order.expire_at) || 0
      }
      // 从充值记录点「继续支付」过来时，直接打开内嵌支付页
      if (queryOrderNo && cachedUrl) {
        this.openPayFrame(cachedUrl)
      }
      // 仅补偿查询一次，后续到账以全局 WS 推送为准
      this.checkOrderStatusOnce(order.order_no)
    },
    onPaid(order) {
      this.clearPendingState()
      this.loadBalance()
      const money = order && order.money ? stripCurrency(order.money) : ''
      this.$toast.success(
        money ? `${this.$t('recharge.pay_success')} +${money}` : this.$t('recharge.pay_success')
      )
      if (this.$route.query && this.$route.query.order_no) {
        this.$router.replace({ path: '/Recharge' })
      }
      Dialog.alert({
        title: this.$t('recharge.pay_success'),
        message: money ? `+${money}` : this.$t('recharge.pay_tip')
      })
    },
    clearPendingState() {
      this.pending = null
      this.payVisible = false
      this.payUrl = ''
      clearPendingOrder()
    },
    handleUnauthorized(res) {
      this.$toast((res && res.msg) || '')
      try {
        localStorage.removeItem('token')
      } catch (e) {
        // ignore
      }
      this.$router.replace({ path: '/Login' })
    }
  }
}
</script>

<style lang='less' scoped>
.container {
  min-height: 100%;
  background: linear-gradient(180deg, #fff5f8 0%, #f8f3ff 55%, #fff6f0 100%);
}

.van-nav-bar {
  background: linear-gradient(120deg, #f3a3c4 0%, #c3a6f0 55%, #ffb894 100%);
  height: 2.88rem;
  position: relative !important;

  /deep/ .van-nav-bar__title {
    color: #fff;
    font-size: 1rem;
  }
  /deep/ .van-nav-bar__left {
    padding: 0 1rem;
  }
  .record {
    font-size: 0.88rem;
    color: #fff;
  }
}

.header {
  padding: 1rem 5% 0;
}

.balance-card {
  background: #fff;
  border-radius: 0.5rem;
  padding: 1rem 1.1rem;
  box-shadow: #ccc 0 0 0.22rem;

  .balance-label {
    color: #969799;
    font-size: 0.8rem;
  }
  .balance-value {
    margin-top: 0.35rem;
    color: #d53dde;
    font-size: 1.8rem;
    font-weight: 600;
    line-height: 1.2;
  }
}

.content {
  padding: 0.75rem 5% 2rem;
}

.pending-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  padding: 0.7rem 1rem;
  border-radius: 0.5rem;
  background: #fff7e6;
  color: #d48806;
  font-size: 0.8rem;

  .pending-action {
    color: #c43fbb;
    font-weight: 600;
    cursor: pointer;
  }
}

.card {
  background: #fff;
  border-radius: 0.5rem;
  padding: 1rem 5%;
  box-shadow: #ccc 0 0 0.22rem;

  .card-title {
    color: #442b89;
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
  }
  .custom-title {
    margin-top: 0.9rem;
  }
  .config-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0 1rem;
    color: #969799;
    font-size: 0.8rem;
  }
  .config-loading span {
    margin-left: 0.4rem;
  }
}

.amount-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.6rem;
}

.amount-empty {
  padding: 0.5rem 0 1rem;
  color: #969799;
  font-size: 0.8rem;
}

.amount-item {
  height: 2.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f0e2f2;
  border-radius: 0.6rem;
  background: #fff;
  color: #4a3a5c;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &.active {
    border-color: #e08ad8;
    background: #fff4fc;
    color: #c43fbb;
    font-weight: 600;
  }
}

.input-wrap {
  display: flex;
  align-items: center;
  height: 3rem;
  padding: 0 1rem;
  border: 1px solid #f0e2f2;
  border-radius: 0.9rem;
  background: #fbfbfc;

  /deep/ .van-cell {
    padding: 0;
    background: transparent;
  }
  /deep/ .van-cell::after {
    display: none;
  }
  /deep/ .van-field {
    flex: 1;
    padding: 0;
    background: transparent;
    font-size: 1rem;
  }
  /deep/ .van-field__control {
    color: #323233;
    font-size: 1rem;
  }
}

.range-tip {
  margin-top: 0.5rem;
  color: #969799;
  font-size: 0.72rem;
}

.tips {
  margin-top: 0.9rem;
  padding: 0.65rem 0.8rem;
  border-radius: 0.5rem;
  background: #faf4ff;
  color: #8a63c9;
  font-size: 0.75rem;
  line-height: 1.45;
}

.button {
  margin-top: 1rem;
  padding: 1rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.9rem;
  background: linear-gradient(120deg, #f7a1c0 0%, #c3a3f2 55%, #ffbe9c 100%);
  color: #fff;
  font-size: 1rem;
  cursor: pointer;

  &.disabled {
    opacity: 0.6;
  }

  .button-text {
    margin-left: 0.4rem;
  }
}

.pay-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}

.pay-panel {
  width: 100%;
  max-width: 31.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0;
  background: #fff;
}

.pay-head {
  flex-shrink: 0;
  box-sizing: border-box;
  height: auto;
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  padding-top: constant(safe-area-inset-top);
  padding-top: env(safe-area-inset-top);
  border-bottom: 1px solid #f0e2f2;
  background: #fff;
  color: #442b89;
  font-size: 0.95rem;
  position: relative;
  z-index: 1;
}

.pay-iframe {
  flex: 1;
  width: 100%;
  min-height: 0;
  display: block;
  border: 0;
  background: #fff;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}


</style>
